import { useEventLogging } from '@innovamat/event-logging';
import { useFlag } from '@innovamat/flags';
import type {
  ContentListType,
  ContentResource,
  Page,
} from '@innovamat/ga-features';
import {
  ContentPage,
  LeadAnnouncementPanel,
  useCurrentNavigationValues,
  useMarkSessionDone,
  useNavigation,
  useUser,
  YearUpgradeAnnouncementPanel,
} from '@innovamat/ga-features';
import { roles } from '@innovamat/radiance-utils';
import { useLocation, useParams } from 'react-router-dom';

type ContentPageRouterProps = {
  title: string;
  pageId?: string;
  isYearUpdate?: boolean;
};

function ContentPageRouter({
  title,
  pageId,
  isYearUpdate,
}: ContentPageRouterProps): JSX.Element | null {
  const { user, isRole } = useUser();
  const { onMarkSession, onToggleSolutions } = useMarkSessionDone();
  const { setEventData } = useEventLogging();
  const { goToResource, navigateInSameNavType } = useNavigation();
  const { classroomId } = useParams();
  const { pathname: fromUrl } = useLocation();
  const { getTypeOfPagePermission, currentPage } = useCurrentNavigationValues();

  const handleResourceClick = (
    resource: ContentResource,
    list: ContentListType,
    page: Page
  ): void => {
    setEventData('resource_accessed_from_list', {
      page_id: page.id,
      page_name: page.name,
      page_type: currentPage ? getTypeOfPagePermission(currentPage) : undefined,
      list_id: list.id,
      list_name: list.name,
      list_type: list.list_type,
      list_order: page.content_lists.indexOf(list) + 1,
      lists_total: page.content_lists.length,
      from_url: fromUrl,
      resource_id: resource.id,
      resource_name: resource.name,
      resource_type: resource.type,
      resource_order_in_list: list.resources.indexOf(resource) + 1,
      resources_total_in_list: list.resources.length,
      is_list_preview: Boolean(list.page_id),
    });

    goToResource({
      type: resource.type,
      resourceId: resource.id,
      listId: list.id,
      pageId: page.id,
      isDigitalGuide: resource.has_digital_guide,
    });
  };

  const handleOnShowMoreClick = (pageId: string, listId: string): void => {
    const route = `/page/${pageId}`;

    setEventData('change_page', {
      page_id: pageId,
      page_name: title,
      from_list_preview_id: listId,
    });

    navigateInSameNavType(route);
  };

  const { value: isPaginationEnabled } = useFlag(
    'showResourcesPagination',
    false,
    {
      identifier: '',
      custom: {
        schoolId: user?.organization?.id || '',
      },
    }
  );

  return (
    <ContentPage
      isPaginationEnabled={isPaginationEnabled}
      title={title}
      pageId={pageId}
      onResourceClick={handleResourceClick}
      onShowMoreClick={handleOnShowMoreClick}
      setEventData={setEventData}
      academicYearId={user?.organization?.academicYearId!}
      classroomId={classroomId}
      onMarkSession={classroomId ? onMarkSession : undefined}
      onToggleSolutions={classroomId ? onToggleSolutions : undefined}
      isStudentView={roles.hasStudentRole(user?.roles!)}
      yearUpdateAnnouncementPanel={
        isYearUpdate ? <YearUpgradeAnnouncementPanel /> : undefined
      }
      leadAnnouncementPanel={
        isRole.Lead ? <LeadAnnouncementPanel /> : undefined
      }
      region={user?.region!}
      language={user?.locale!}
    />
  );
}

export { ContentPageRouter };
